/**
 * GPS Data Caching Utilities
 * Provides caching functionality for GPS device data with memory management
 */

import type { DeviceGpsInfo } from '../../gps-websocket/types';

/**
 * GPS Data Cache entry interface
 */
interface GpsCacheEntry {
  data: DeviceGpsInfo[];
  timestamp: Date;
  source: 'websocket' | 'api' | 'manual';
  updateCount: number;
}

/**
 * GPS Data Cache statistics interface
 */
interface GpsCacheStats {
  totalEntries: number;
  memoryUsageBytes: number;
  oldestEntry: Date | null;
  newestEntry: Date | null;
  hitCount: number;
  missCount: number;
  hitRatio: number;
}

/**
 * GPS Data Cache configuration interface
 */
interface GpsCacheConfig {
  maxEntries: number;           // 최대 캐시 엔트리 수
  maxAge: number;               // 최대 보관 시간 (ms)
  enableCompression: boolean;   // 데이터 압축 활성화
  autoCleanup: boolean;         // 자동 정리 활성화
  cleanupInterval: number;      // 정리 주기 (ms)
}

/**
 * GPS Data Cache class
 * Manages caching of GPS device data with LRU eviction and automatic cleanup
 */
export class GpsDataCache {
  private cache: Map<string, GpsCacheEntry> = new Map();
  private accessOrder: string[] = [];
  private config: GpsCacheConfig;
  private stats: GpsCacheStats;
  private cleanupTimer: NodeJS.Timeout | null = null;

  /**
   * Create GPS data cache instance
   * @param config - Cache configuration
   */
  constructor(config: Partial<GpsCacheConfig> = {}) {
    this.config = {
      maxEntries: config.maxEntries || 100,
      maxAge: config.maxAge || 300000, // 5분
      enableCompression: config.enableCompression || false,
      autoCleanup: config.autoCleanup || true,
      cleanupInterval: config.cleanupInterval || 60000 // 1분
    };
    
    this.stats = {
      totalEntries: 0,
      memoryUsageBytes: 0,
      oldestEntry: null,
      newestEntry: null,
      hitCount: 0,
      missCount: 0,
      hitRatio: 0
    };
    
    if (this.config.autoCleanup) {
      this.startAutoCleanup();
    }
  }

  /**
   * Store GPS data in cache
   * @param key - Cache key
   * @param data - GPS device data
   * @param source - Data source
   */
  set(key: string, data: DeviceGpsInfo[], source: 'websocket' | 'api' | 'manual' = 'websocket'): void {
    const entry: GpsCacheEntry = {
      data: this.config.enableCompression ? this.compressData(data) : [...data],
      timestamp: new Date(),
      source,
      updateCount: this.cache.has(key) ? this.cache.get(key)!.updateCount + 1 : 1
    };
    
    // LRU 관리: 기존 키가 있으면 순서에서 제거
    if (this.cache.has(key)) {
      this.removeFromAccessOrder(key);
    }
    
    // 새 엔트리 추가
    this.cache.set(key, entry);
    this.accessOrder.push(key);
    
    // 크기 제한 확인 및 오래된 엔트리 제거
    this.enforceMaxEntries();
    
    // 통계 업데이트
    this.updateStats();
  }

  /**
   * Retrieve GPS data from cache
   * @param key - Cache key
   * @returns Cached GPS data or null
   */
  get(key: string): DeviceGpsInfo[] | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.stats.missCount++;
      this.updateHitRatio();
      return null;
    }
    
    // 만료 확인
    if (this.isExpired(entry)) {
      this.delete(key);
      this.stats.missCount++;
      this.updateHitRatio();
      return null;
    }
    
    // LRU 업데이트: 최근 사용된 것으로 이동
    this.moveToEnd(key);
    
    this.stats.hitCount++;
    this.updateHitRatio();
    
    return this.config.enableCompression ? 
           this.decompressData(entry.data) : 
           [...entry.data];
  }

  /**
   * Check if key exists in cache and is not expired
   * @param key - Cache key
   * @returns True if key exists and is valid
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    return entry !== undefined && !this.isExpired(entry);
  }

  /**
   * Delete entry from cache
   * @param key - Cache key
   */
  delete(key: string): boolean {
    const deleted = this.cache.delete(key);
    if (deleted) {
      this.removeFromAccessOrder(key);
      this.updateStats();
    }
    return deleted;
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
    this.updateStats();
  }

  /**
   * Get cache statistics
   * @returns Cache statistics
   */
  getStats(): GpsCacheStats {
    return { ...this.stats };
  }

  /**
   * Get all cache keys
   * @returns Array of cache keys
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Get cached data for multiple keys
   * @param keys - Array of cache keys
   * @returns Map of key to cached data
   */
  getMultiple(keys: string[]): Map<string, DeviceGpsInfo[]> {
    const result = new Map<string, DeviceGpsInfo[]>();
    
    keys.forEach(key => {
      const data = this.get(key);
      if (data) {
        result.set(key, data);
      }
    });
    
    return result;
  }

  /**
   * Set cache configuration
   * @param newConfig - New configuration
   */
  updateConfig(newConfig: Partial<GpsCacheConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // 자동 정리 재시작
    if (this.config.autoCleanup && !this.cleanupTimer) {
      this.startAutoCleanup();
    } else if (!this.config.autoCleanup && this.cleanupTimer) {
      this.stopAutoCleanup();
    }
  }

  /**
   * Manually trigger cache cleanup
   */
  cleanup(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];
    
    this.cache.forEach((entry, key) => {
      if (this.isExpired(entry)) {
        expiredKeys.push(key);
      }
    });
    
    expiredKeys.forEach(key => {
      this.delete(key);
    });
    
    console.log(`🧹 GPS Cache cleanup: removed ${expiredKeys.length} expired entries`);
  }

  /**
   * Destroy cache and cleanup resources
   */
  destroy(): void {
    this.stopAutoCleanup();
    this.clear();
  }

  /**
   * Check if cache entry is expired
   * @param entry - Cache entry
   * @returns True if expired
   */
  private isExpired(entry: GpsCacheEntry): boolean {
    const age = Date.now() - entry.timestamp.getTime();
    return age > this.config.maxAge;
  }

  /**
   * Enforce maximum cache entries by removing oldest
   */
  private enforceMaxEntries(): void {
    while (this.cache.size > this.config.maxEntries) {
      const oldestKey = this.accessOrder[0];
      if (oldestKey) {
        this.delete(oldestKey);
      } else {
        break;
      }
    }
  }

  /**
   * Move key to end of access order (most recently used)
   * @param key - Cache key
   */
  private moveToEnd(key: string): void {
    this.removeFromAccessOrder(key);
    this.accessOrder.push(key);
  }

  /**
   * Remove key from access order
   * @param key - Cache key
   */
  private removeFromAccessOrder(key: string): void {
    const index = this.accessOrder.indexOf(key);
    if (index !== -1) {
      this.accessOrder.splice(index, 1);
    }
  }

  /**
   * Update cache statistics
   */
  private updateStats(): void {
    this.stats.totalEntries = this.cache.size;
    this.stats.memoryUsageBytes = this.calculateMemoryUsage();
    
    // 가장 오래된/최신 엔트리 찾기
    let oldest: Date | null = null;
    let newest: Date | null = null;
    
    this.cache.forEach(entry => {
      if (!oldest || entry.timestamp < oldest) {
        oldest = entry.timestamp;
      }
      if (!newest || entry.timestamp > newest) {
        newest = entry.timestamp;
      }
    });
    
    this.stats.oldestEntry = oldest;
    this.stats.newestEntry = newest;
  }

  /**
   * Update hit ratio
   */
  private updateHitRatio(): void {
    const total = this.stats.hitCount + this.stats.missCount;
    this.stats.hitRatio = total > 0 ? (this.stats.hitCount / total) * 100 : 0;
  }

  /**
   * Calculate approximate memory usage
   * @returns Memory usage in bytes (approximate)
   */
  private calculateMemoryUsage(): number {
    let totalSize = 0;
    
    this.cache.forEach((entry, key) => {
      // Key size
      totalSize += key.length * 2; // UTF-16
      
      // Entry metadata size (approximate)
      totalSize += 100; // timestamp, source, updateCount
      
      // Data size (approximate)
      totalSize += entry.data.length * 200; // ~200 bytes per device (approximate)
    });
    
    return totalSize;
  }

  /**
   * Start automatic cache cleanup
   */
  private startAutoCleanup(): void {
    if (this.cleanupTimer) return;
    
    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * Stop automatic cache cleanup
   */
  private stopAutoCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Compress GPS data (simplified - in real implementation, use proper compression)
   * @param data - GPS device data
   * @returns Compressed data
   */
  private compressData(data: DeviceGpsInfo[]): DeviceGpsInfo[] {
    // 실제 구현에서는 JSON 압축 또는 다른 압축 알고리즘 사용
    // 여기서는 단순히 불필요한 필드 제거로 시뮬레이션
    return data.map(device => ({
      psn: device.psn,
      latitude: device.latitude,
      longitude: device.longitude,
      active: device.active,
      share_video: device.share_video,
      speed: device.speed,
      dir_angle: device.dir_angle,
      // dev_name 등 부가 정보는 압축 시 제거
    })) as DeviceGpsInfo[];
  }

  /**
   * Decompress GPS data
   * @param data - Compressed GPS device data
   * @returns Decompressed data
   */
  private decompressData(data: DeviceGpsInfo[]): DeviceGpsInfo[] {
    // 압축 해제 로직 (현재는 단순 복사)
    return [...data];
  }
}

/**
 * Create a GPS data cache with default configuration
 * @param config - Optional cache configuration
 * @returns GPS data cache instance
 */
export function createGpsDataCache(config?: Partial<GpsCacheConfig>): GpsDataCache {
  return new GpsDataCache(config);
}

/**
 * Generate cache key for GPS data
 * @param userEmail - User email
 * @param psnList - List of PSNs
 * @param filter - Additional filter criteria
 * @returns Cache key string
 */
export function generateCacheKey(
  userEmail: string,
  psnList?: string[],
  filter?: string
): string {
  const parts = [userEmail];
  
  if (psnList && psnList.length > 0) {
    parts.push(psnList.sort().join(','));
  }
  
  if (filter) {
    parts.push(filter);
  }
  
  return parts.join('|');
}

/**
 * Cache key generators for different data types
 */
export const CacheKeys = {
  /**
   * Generate key for multi live view data
   */
  multiLiveView: (userEmail: string, psnList: string[]) => 
    generateCacheKey(userEmail, psnList, 'multiLiveView'),
  
  /**
   * Generate key for user devices data
   */
  myDevices: (userEmail: string) => 
    generateCacheKey(userEmail, undefined, 'myDevices'),
  
  /**
   * Generate key for zone data
   */
  zone: (userEmail: string, sw: string, ne: string) => 
    generateCacheKey(userEmail, undefined, `zone_${sw}_${ne}`),
  
  /**
   * Generate key for specific device data
   */
  specificDevice: (userEmail: string, psn: string) => 
    generateCacheKey(userEmail, [psn], 'specific'),
  
  /**
   * Generate key for filtered data
   */
  filtered: (userEmail: string, filterHash: string) => 
    generateCacheKey(userEmail, undefined, `filtered_${filterHash}`)
  ,
  
  /**
   * Generate key for zone no auth data
   */
  zoneNoAuth: (sw: string, ne: string) => 
    generateCacheKey('public', undefined, `zoneNoAuth_${sw}_${ne}`),
  
  /**
   * Generate key for zone all devices data
   */
  zoneAllDevices: (userEmail: string, sw: string, ne: string) => 
    generateCacheKey(userEmail, undefined, `zoneAllDevices_${sw}_${ne}`),
  
  /**
   * Generate key for group zone data
   */
  groupZone: (userEmail: string, sw: string, ne: string, groupIdList: string[], type: 'my' | 'all') => 
    generateCacheKey(userEmail, groupIdList, `groupZone_${type}_${sw}_${ne}`)
};